import { extendTheme } from "@mui/joy";
import { createThemeLayout } from "./layout";
import { xsButtonRoot, xsIconButtonRoot } from "./xsButton";
import { SELECTION_TINT_FAINT } from "../components/documents/styles/rowTokens";

/** The classic web palette and Joy defaults, retained while Design v2 is off. */
const lightPalette = {
    primary: {
        "50": "#d3e0fb",
        "100": "#d3e0fb",
        "200": "#a8c1f7",
        "300": "#7ca1f3",
        "400": "#5182ef",
        "500": "#2563eb",
        "600": "#1e4fbc",
        "700": "#163b8d",
        "800": "#0f285e",
        "900": "#07142f",
    },
    neutral: {
        "50": "#f8fafc",
        "100": "#f1f5f9",
        "200": "#e2e8f0",
        "300": "#cbd5e1",
        "400": "#94a3b8",
        "500": "#64748b",
        "600": "#475569",
        "700": "#334155",
        "800": "#1e293b",
        "900": "#0f172a",
    },
    danger: {
        "50": "#fef2f2",
        "100": "#fee2e2",
        "200": "#fecaca",
        "300": "#fca5a5",
        "400": "#f87171",
        "500": "#ef4444",
        "600": "#dc2626",
        "700": "#b91c1c",
        "800": "#991b1b",
        "900": "#7f1d1d",
    },
    success: {
        "50": "#f0fdf4",
        "100": "#dcfce7",
        "200": "#bbf7d0",
        "300": "#86efac",
        "400": "#4ade80",
        "500": "#22c55e",
        "600": "#16a34a",
        "700": "#15803d",
        "800": "#166534",
        "900": "#14532d",
    },
    warning: {
        "50": "#fff7ed",
        "100": "#ffedd5",
        "200": "#fed7aa",
        "300": "#fdba74",
        "400": "#fb923c",
        "500": "#f97316",
        "600": "#ea580c",
        "700": "#c2410c",
        "800": "#9a3412",
        "900": "#7c2d12",
    },
    background: {
        surface: "#fff",
        level1: "#f8fafc",
        level2: "#f1f5f9",
        level3: "#e2e8f0",
        // primeTheme's glass over the project's photo, on this palette's surface.
        glass: "color-mix(in srgb, #fff 64%, transparent)",
    },
    // primeTheme's starred star and its outline.
    star: "#EAB308",
    starEdge: "#A16207",
};

/** App body font — Joy `fontFamily.body`. */
export const BODY_FONT_FAMILY = "'Inter', system-ui, var(--joy-fontFamily-fallback)";

/** Heading / display font — Overpass (700/800), falling back to the body stack. */
export const HEADING_FONT_FAMILY = "'Overpass', 'Inter', system-ui, var(--joy-fontFamily-fallback)";

export const classicTheme = extendTheme({
    colorSchemes: {
        light: {
            palette: lightPalette,
        },
    },
    components: {
        // The viewer's footer is shared with Design v2: its extra-small tools (`theme/xsButton`).
        JoyButton: {
            styleOverrides: {
                root: ({ ownerState, theme }) => xsButtonRoot(ownerState.size, theme),
            },
        },
        JoyIconButton: {
            styleOverrides: {
                root: ({ ownerState, theme }) => xsIconButtonRoot(ownerState.size, theme),
            },
        },
        // @mui/joy@5.0.0-beta.52 ships Switch's invisible <input> as
        // position:absolute with NO top/left, so it lands at its static position —
        // one full track-width to the RIGHT of the visible switch. Clicking the
        // drawn track then does nothing (it hits the inert action div) while the
        // empty space beside it toggles. Fixed upstream in later Joy releases; pin
        // the input over the track until the beta is upgraded.
        JoySwitch: {
            styleOverrides: {
                input: {
                    top: 0,
                    left: 0,
                },
            },
        },
        // Register table look — the default for every JoyTable in the app.
        // Local exceptions override individual vars/rules at the call site
        // (see e.g. `sheetStyles.drawingSetRow`'s `--TableRow-hoverBackground`).
        JoyTable: {
            styleOverrides: {
                root: ({ theme }) => ({
                    "--TableRow-hoverBackground": SELECTION_TINT_FAINT,
                    "--TableCell-headBackground": "var(--joy-palette-background-surface)",
                    // Parity with the old sx `py: 1, px: 0.5`. Joy's builtin td/th
                    // padding rule reads these vars, so a call site overrides the
                    // DATA (the var) instead of fighting the declaration.
                    "--TableCell-paddingX": "4px",
                    "--TableCell-paddingY": "8px",
                    // Parity with the old sx `pl/pr: 3`: 24px first/last-column edge
                    // inset. These longhand rules outrank Joy's builtin padding
                    // shorthand AND any call-site `& td` padding, so tables with
                    // their own padding scheme must opt out by setting
                    // --TableCell-gutterX locally (0 to remove, or their paddingX
                    // value for uniform columns) — see e.g. ReviewGrid,
                    // DisciplineMapSettings, SectionGroupRevisionsRow.
                    "& td:first-child, & th:first-child": {
                        paddingLeft: `var(--TableCell-gutterX, ${theme.spacing(3)})`,
                    },
                    "& td:last-child, & th:last-child": {
                        paddingRight: `var(--TableCell-gutterX, ${theme.spacing(3)})`,
                    },
                    // Register look: quiet uppercase column labels on a plain white
                    // band (--TableCell-headBackground above), a single hairline
                    // (theme divider) below — never a grey fill. Explicit
                    // borderBottom (not --Table-headerUnderlineThickness) because
                    // several register tables use borderAxis="none" and would
                    // otherwise lose the hairline.
                    "& thead th": {
                        color: theme.vars.palette.text.tertiary,
                        fontSize: "10.5px",
                        fontWeight: theme.vars.fontWeight.lg,
                        textTransform: "uppercase",
                        letterSpacing: "0.09em",
                        borderBottom: "1px solid",
                        borderColor: theme.vars.palette.divider,
                        height: "36px",
                    },
                    // Register row height; height on a td is a minimum, taller
                    // content (sheet thumbnails) still grows.
                    "& tbody td": { height: "40px" },
                    // Row actions stay faded out until the row is hovered or holds
                    // keyboard focus; layout is reserved so rows never shift. Hidden via
                    // opacity (not visibility) so the action button stays in the tab
                    // order and hit-testable — a visibility:hidden button can never
                    // receive focus or a tap, so Tab (or touch) could never reach it to
                    // trigger its own reveal.
                    "& .rowActions": {
                        opacity: 0,
                    },
                    "& tr:hover .rowActions, & tr:focus-within .rowActions": {
                        opacity: 1,
                    },
                }),
            },
        },
    },
    fontFamily: {
        body: BODY_FONT_FAMILY,
        display: HEADING_FONT_FAMILY,
        code: "'SF Mono', ui-monospace, Menlo, Consolas, monospace",
    },
});

classicTheme.layout = {
    ...createThemeLayout(classicTheme.spacing),
    row: { default: "32px", media: "40px", group: "32px", menu: "32px" },
    control: "32px",
};
Object.assign(classicTheme.typography, {
    display: classicTheme.typography.h1,
    title: classicTheme.typography.h3,
    heading: classicTheme.typography["title-md"],
    body: classicTheme.typography["body-md"],
    secondary: classicTheme.typography["body-sm"],
    caption: classicTheme.typography["body-xs"],
    overline: classicTheme.typography["body-xs"],
});
